import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {format} from 'date-fns'
import {DatePicker} from '@material-ui/pickers'
import axios from 'axios'
import {useTranslation} from 'react-i18next'

import {KTSVG} from '../../../../../../../_metronic/helpers'
import {useErrorHandler} from '../../../../../../../_metronic/helpers/ErrorHandler'
import {useMapLayersV2} from '../../../../Map/MapLayersV2Context'
import {mapState} from '../../../../Map/MapState'
import {paintAmbientGeojson} from '../Ambients/ambientMapUtils'
import {useMapLegendContext} from '../Helpers/MapLegend'
import {useViewAmbientContext} from '../Ambients'
import {buildAltimetryRaster} from '../Images/Services/Altimetry/Altimetry'
import {
  createSoilMapCanvas,
  getSoilMapBbox,
  getSoilMapLegendModel,
} from '../Images/Services/SoilMap/soilMapVisuals'
import {useViewImageContext} from '../Images/ViewImageContext'
import {viewImageState} from '../Images/ViewImageState'
import {useViewLandContext} from '../ViewLandContext'
import {
  isComparisonHdProductDisabled,
  isComparisonLayerDisabled,
  shouldEnhanceComparisonImageLayer,
} from './comparisonLayerAvailability'
import {
  getComparisonLayerSelectValue,
  parseComparisonLayerSelectValue,
} from './comparisonLayerSelection'
import {
  getComparisonLegendItems,
  normalizeComparisonImageLegend,
} from './comparisonLegendModel'
import {SWIPE_SIDE_LEFT, getComparisonMapComposition} from './comparisonMapComposition'
import ComparisonHdDatePicker from './ComparisonHdDatePicker'
import {useLandOperationSummary} from '../useLandOperationSummary'
import {PLANET_HD_V1_ENABLED} from '../../../../../../config/featureFlags'
import {
  REMOTE_SENSING_DATE_STATES,
  canPrepareRemoteSensingDate,
  isRemoteSensingDateVisualizable,
} from '../Images/remoteSensingDateModel'
import {PLANET_HD_ACCESS_STATES} from '../Images/planetHdAccessModel'
import {doesPlanetHdSelectionMatchAccess} from '../Images/planetHdAccessIdentity'
import {selectBestPlanetHdItemsByDate} from '../Images/planetHdCalendarModel'
import {
  REMOTE_SENSING_PRODUCTS,
  getImageTypeForSource,
  normalizeRemoteSensingSource,
} from '../Images/remoteSensingModel'
import {
  buildImageLayerCacheOptions,
  requestRemoteSensingLayer,
} from '../Images/remoteSensingLayerRequest'
import {getLayerBbox} from '../Images/Services/imageLayerPayload'
import * as GisDataAdapter from '../../../services/GisDataAdapter'

const GOOGLE_MAP_IMAGE_TYPE = 'google_map'
const LAYER_TYPE_ALTIMETRY = 'altimetry'
const LAYER_TYPE_SOIL_MAP = 'soil_map'
const LAYER_TYPE_YIELD_MAP = 'yield_map'
const LAYER_TYPE_AMBIENT = 'ambient'
const LAYER_TYPE_IMAGE_AMBIENT = 'imageAmbient'
const LAYER_TYPE_AMBIENT_HD = 'ambientHd'
const LAYER_TYPE_IMAGE_AMBIENT_HD = 'imageAmbientHd'
const COMPARISON_METHOD_SWIPE = 'swipe'
const COMPARISON_METHOD_OPACITY = 'opacity'
const DATE_PICKER_TARGET_BASE = 'base'
const DATE_PICKER_TARGET_COMPARE = 'compare'
const IMAGE_TYPE_OPTIONS = ['rgb', 'ndvi', 'evi', 'ndre']
const SENTINEL_IMAGE_TYPE_OPTIONS = ['rgb', 'ndvi', 'evi']
const PLANET_HD_IMAGE_TYPE_OPTIONS = ['rgb', 'ndvi', 'evi', 'ndre']
const BASE_NON_IMAGE_LAYER_OPTIONS = [
  GOOGLE_MAP_IMAGE_TYPE,
  LAYER_TYPE_ALTIMETRY,
  LAYER_TYPE_SOIL_MAP,
  LAYER_TYPE_YIELD_MAP,
  LAYER_TYPE_AMBIENT,
  LAYER_TYPE_IMAGE_AMBIENT,
]
const COMPARE_NON_IMAGE_LAYER_OPTIONS = [
  GOOGLE_MAP_IMAGE_TYPE,
  LAYER_TYPE_ALTIMETRY,
  LAYER_TYPE_SOIL_MAP,
  LAYER_TYPE_YIELD_MAP,
  LAYER_TYPE_AMBIENT,
  LAYER_TYPE_IMAGE_AMBIENT,
]
const IMAGE_COMPARE_LAYER_BASE = 'comparison-image-base'
const IMAGE_COMPARE_LAYER_OVERLAY = 'comparison-image-overlay'
const IMAGE_COMPARE_LAYER_DIVIDER = 'comparison-image-divider'
const IMAGE_AMBIENT_LAYER = 'comparison-image-ambient-image'
const AMBIENT_COMPARE_LAYER = 'comparison-ambient-base'
const ALTIMETRY_COMPARE_LAYER = 'comparison-altimetry'

const getLayerId = (base, landId) => `${base}-${landId}`

const DEFAULT_COMPARISON_STATE = {
  selectedMode: COMPARISON_METHOD_SWIPE,
  imageType: '',
  compareImageType: '',
  baseProduct: REMOTE_SENSING_PRODUCTS.SENTINEL,
  compareProduct: REMOTE_SENSING_PRODUCTS.SENTINEL,
  baseDate: '',
  compareDate: '',
  baseAcquisitionId: '',
  baseItemId: '',
  compareAcquisitionId: '',
  compareItemId: '',
  baseCloudinessFilter: 20,
  compareCloudinessFilter: 20,
  splitPercent: 50,
  ambientType: '',
  imageOpacity: 62,
}

const isImageLayerType = (layerType) => IMAGE_TYPE_OPTIONS.includes(layerType)

const isEnhanceableImageLayer = (layerType) => ['evi', 'ndvi', 'ndre'].includes(layerType)

const isAmbientLayerType = (layerType) =>
  [
    LAYER_TYPE_AMBIENT,
    LAYER_TYPE_IMAGE_AMBIENT,
    LAYER_TYPE_AMBIENT_HD,
    LAYER_TYPE_IMAGE_AMBIENT_HD,
  ].includes(layerType)

const isPlanetHdComparisonProduct = (product) => (
  PLANET_HD_V1_ENABLED && product === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
)

const getComparisonImageTypes = (product) => (
  isPlanetHdComparisonProduct(product)
    ? PLANET_HD_IMAGE_TYPE_OPTIONS
    : SENTINEL_IMAGE_TYPE_OPTIONS
)

const getComparisonSource = (product, imageType) => normalizeRemoteSensingSource({
  product,
  layer: getRemoteLayer(imageType),
})

const isLayerWithoutDate = (layerType) =>
  layerType === GOOGLE_MAP_IMAGE_TYPE ||
  layerType === LAYER_TYPE_ALTIMETRY ||
  layerType === LAYER_TYPE_SOIL_MAP ||
  layerType === LAYER_TYPE_YIELD_MAP ||
  isAmbientLayerType(layerType)

const comparisonStateByLand = new Map()

const clearLegacyStoredComparisonState = () => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith('specific.land.comparison.'))
      .forEach((key) => window.sessionStorage.removeItem(key))
  } catch (error) {
    // Storage can be unavailable in private contexts; comparison still works in memory.
  }
}

clearLegacyStoredComparisonState()

const getSerializableComparisonState = (state) => ({
  selectedMode: state.selectedMode,
  imageType: state.imageType,
  compareImageType: state.compareImageType,
  baseProduct: state.baseProduct,
  compareProduct: state.compareProduct,
  baseDate: state.baseDate,
  compareDate: state.compareDate,
  baseAcquisitionId: state.baseAcquisitionId,
  baseItemId: state.baseItemId,
  compareAcquisitionId: state.compareAcquisitionId,
  compareItemId: state.compareItemId,
  baseCloudinessFilter: state.baseCloudinessFilter,
  compareCloudinessFilter: state.compareCloudinessFilter,
  splitPercent: state.splitPercent,
  ambientType: state.ambientType,
  imageOpacity: state.imageOpacity,
})

const readStoredComparisonState = (landId) => {
  if (!landId) {
    return null
  }

  return comparisonStateByLand.get(landId) || null
}

const normalizeComparisonState = (state) => {
  const nextState = {...state}

  const legacyCloudinessFilter = Number.isFinite(Number(nextState.cloudinessFilter))
    ? Number(nextState.cloudinessFilter)
    : 20
  if (!Number.isFinite(Number(nextState.baseCloudinessFilter))) {
    nextState.baseCloudinessFilter = legacyCloudinessFilter
  }
  if (!Number.isFinite(Number(nextState.compareCloudinessFilter))) {
    nextState.compareCloudinessFilter = legacyCloudinessFilter
  }
  delete nextState.cloudinessFilter

  if (!PLANET_HD_V1_ENABLED) {
    nextState.baseProduct = REMOTE_SENSING_PRODUCTS.SENTINEL
    nextState.compareProduct = REMOTE_SENSING_PRODUCTS.SENTINEL
    nextState.baseAcquisitionId = ''
    nextState.baseItemId = ''
    nextState.compareAcquisitionId = ''
    nextState.compareItemId = ''
  }

  if (!getComparisonImageTypes(nextState.baseProduct).includes(nextState.imageType)) {
    if (nextState.imageType === 'ndre') {
      nextState.imageType = ''
      nextState.baseDate = ''
    }
  }

  if (!getComparisonImageTypes(nextState.compareProduct).includes(nextState.compareImageType)) {
    if (nextState.compareImageType === 'ndre') {
      nextState.compareImageType = ''
      nextState.compareDate = ''
    }
  }

  if (nextState.selectedMode === 'images') {
    nextState.selectedMode = COMPARISON_METHOD_SWIPE
  } else if (nextState.selectedMode === 'image_ambient') {
    nextState.selectedMode = COMPARISON_METHOD_OPACITY
  }

  if (
    nextState.selectedMode !== COMPARISON_METHOD_SWIPE &&
    nextState.selectedMode !== COMPARISON_METHOD_OPACITY
  ) {
    nextState.selectedMode = DEFAULT_COMPARISON_STATE.selectedMode
  }

  if (!nextState.compareImageType && nextState.ambientType) {
    nextState.compareImageType = nextState.ambientType
  }

  return nextState
}

const getPersistedComparisonState = (landId) =>
  normalizeComparisonState({
    ...DEFAULT_COMPARISON_STATE,
    ...readStoredComparisonState(landId),
  })

const persistComparisonState = (landId, state) => {
  if (!landId) {
    return
  }

  const nextState = getSerializableComparisonState(state)

  comparisonStateByLand.set(landId, nextState)
}

const normalizeDateValue = (date) => {
  if (!date) {
    return ''
  }

  if (typeof date === 'string') {
    return date.slice(0, 10)
  }

  return format(date, 'yyyy-MM-dd')
}

const getRemoteLayer = (imageType) => {
  return imageType === 'rgb' ? 'visible' : imageType
}

const getMapImageType = (imageType) => {
  return imageType === 'rgb' ? 'visible' : imageType
}

const getImageTypeLabelKey = (imageType) => {
  return imageType === 'rgb' ? 'visible' : imageType
}

const toDateLabel = (dateValue) => {
  if (!dateValue) {
    return '-'
  }

  return format(new Date(`${dateValue}T00:00:00`), 'dd/MM/yyyy')
}

const clampCloudinessFilter = (value) => {
  const numericValue = Number(value)

  if (Number.isNaN(numericValue)) {
    return 0
  }

  return Math.min(Math.max(Math.round(numericValue), 0), 100)
}

const toDecimalCommaLabel = (value) => `${value}`.replace('.', ',')

const clampSplitRatio = (value) => Math.min(Math.max(value, 0), 1)

const interpolateCoordinate = (from, to, ratio) => {
  return [
    from[0] + (to[0] - from[0]) * ratio,
    from[1] + (to[1] - from[1]) * ratio,
  ]
}

const getDividerCoordinates = (bbox, splitPercent) => {
  const ratio = clampSplitRatio(splitPercent / 100)

  return [
    interpolateCoordinate(bbox[0], bbox[1], ratio),
    interpolateCoordinate(bbox[3], bbox[2], ratio),
  ]
}

const getCoordinateMidpoint = (from, to) => [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2]

const collectCoordinatePairs = (value, output = []) => {
  if (!Array.isArray(value)) {
    return output
  }

  if (
    value.length >= 2 &&
    Number.isFinite(Number(value[0])) &&
    Number.isFinite(Number(value[1]))
  ) {
    output.push([Number(value[0]), Number(value[1])])
    return output
  }

  value.forEach((item) => collectCoordinatePairs(item, output))
  return output
}

const getPolygonImageBbox = (polygons) => {
  const coordinates = collectCoordinatePairs(polygons)

  if (coordinates.length < 3) {
    return null
  }

  const lngs = coordinates.map(([lng]) => lng)
  const lats = coordinates.map(([, lat]) => lat)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)

  if (minLng === maxLng || minLat === maxLat) {
    return null
  }

  return [
    [minLng, maxLat],
    [maxLng, maxLat],
    [maxLng, minLat],
    [minLng, minLat],
  ]
}

const getMapLandGeometry = (landId) => {
  if (!landId || !mapState.isInitialized()) {
    return null
  }

  try {
    return mapState.source().getFeature(landId)?.geometry || null
  } catch (error) {
    return null
  }
}

const waitForMapInitialized = () =>
  new Promise((resolve) => {
    const wait = () => {
      if (mapState.isInitialized()) {
        resolve()
        return
      }

      window.setTimeout(wait, 100)
    }

    wait()
  })

const getProjectedPoint = (coordinate) => {
  const map = mapState.map

  if (!map) {
    return null
  }

  const point = map.project(coordinate)
  const rect = map.getContainer().getBoundingClientRect()

  return {
    x: rect.left + point.x,
    y: rect.top + point.y,
  }
}

const getDividerGuideGeometry = (bbox, splitPercent) => {
  const [top, bottom] = getDividerCoordinates(bbox, splitPercent)
  const topPoint = getProjectedPoint(top)
  const bottomPoint = getProjectedPoint(bottom)

  if (!topPoint || !bottomPoint) {
    return null
  }

  const deltaX = bottomPoint.x - topPoint.x
  const deltaY = bottomPoint.y - topPoint.y

  return {
    x: topPoint.x,
    y: topPoint.y,
    length: Math.sqrt(deltaX * deltaX + deltaY * deltaY),
    angle: Math.atan2(deltaY, deltaX) * (180 / Math.PI) - 90,
  }
}

const getSplitPercentFromPointer = ({bbox, clientX, clientY}) => {
  const map = mapState.map

  if (!map) {
    return null
  }

  const rect = map.getContainer().getBoundingClientRect()
  const leftMidpoint = getCoordinateMidpoint(bbox[0], bbox[3])
  const rightMidpoint = getCoordinateMidpoint(bbox[1], bbox[2])
  const leftPoint = map.project(leftMidpoint)
  const rightPoint = map.project(rightMidpoint)
  const pointer = {
    x: clientX - rect.left,
    y: clientY - rect.top,
  }
  const vector = {
    x: rightPoint.x - leftPoint.x,
    y: rightPoint.y - leftPoint.y,
  }
  const lengthSquared = vector.x * vector.x + vector.y * vector.y

  if (lengthSquared === 0) {
    return null
  }

  const ratio =
    ((pointer.x - leftPoint.x) * vector.x + (pointer.y - leftPoint.y) * vector.y) /
    lengthSquared

  return clampSplitRatio(ratio) * 100
}

const createDrawableImage = async (blob) => {
  if (typeof window.createImageBitmap === 'function') {
    return window.createImageBitmap(blob)
  }

  return new Promise((resolve, reject) => {
    const sourceUrl = URL.createObjectURL(blob)
    const image = new Image()
    const timeoutId = window.setTimeout(() => {
      URL.revokeObjectURL(sourceUrl)
      reject(new Error('comparison-image-timeout'))
    }, 10000)

    image.onload = () => {
      window.clearTimeout(timeoutId)
      URL.revokeObjectURL(sourceUrl)
      resolve(image)
    }

    image.onerror = () => {
      window.clearTimeout(timeoutId)
      URL.revokeObjectURL(sourceUrl)
      reject(new Error('comparison-image-load-failed'))
    }

    image.src = sourceUrl
  })
}

const parseBlobJson = async (blob) => JSON.parse(await blob.text())

const disposeDrawableImage = (drawable) => {
  drawable?.close?.()
}

const paintSwipeCanvas = (source, splitPercent) => {
  const drawable = source?.overlayDrawable

  if (!drawable) {
    return null
  }

  const width = drawable.width
  const height = drawable.height
  const ratio = clampSplitRatio(splitPercent / 100)
  const clipX = width * ratio
  const visibleSide = source.swipeSide === SWIPE_SIDE_LEFT ? SWIPE_SIDE_LEFT : 'right'
  const visibleStart = visibleSide === SWIPE_SIDE_LEFT ? 0 : clipX
  const visibleWidth = visibleSide === SWIPE_SIDE_LEFT ? clipX : width - clipX
  const canvas = source.overlayCanvas || document.createElement('canvas')

  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width
    canvas.height = height
  }

  const context = canvas.getContext('2d')
  context.clearRect(0, 0, width, height)

  if (visibleWidth > 0.5) {
    context.save()
    context.beginPath()
    context.rect(visibleStart, 0, visibleWidth, height)
    context.clip()
    context.drawImage(drawable, 0, 0, width, height)
    context.restore()
  }

  source.overlayCanvas = canvas
  return canvas
}

const createCanvasFromDrawable = (drawable) => {
  const canvas = document.createElement('canvas')
  canvas.width = drawable.width
  canvas.height = drawable.height

  const context = canvas.getContext('2d')
  context.drawImage(drawable, 0, 0, canvas.width, canvas.height)

  return canvas
}

const getCanvasPointFromCoordinate = ([lng, lat], bbox, width, height) => {
  const minLng = bbox[0][0]
  const maxLng = bbox[1][0]
  const maxLat = bbox[0][1]
  const minLat = bbox[2][1]
  const lngSpan = maxLng - minLng
  const latSpan = maxLat - minLat

  if (!lngSpan || !latSpan) {
    return null
  }

  return {
    x: ((lng - minLng) / lngSpan) * width,
    y: ((maxLat - lat) / latSpan) * height,
  }
}

const getAmbientFeaturePolygons = (geometry) => {
  if (geometry?.type === 'Polygon') {
    return [geometry.coordinates]
  }

  if (geometry?.type === 'MultiPolygon') {
    return geometry.coordinates
  }

  return []
}

const getAmbientCanvasSize = (bbox) => {
  const lngSpan = Math.abs(bbox[1][0] - bbox[0][0]) || 1
  const latSpan = Math.abs(bbox[0][1] - bbox[2][1]) || 1
  const width = 1024
  const height = Math.min(Math.max(Math.round(width * (latSpan / lngSpan)), 256), 1600)

  return {height, width}
}

const createCanvasFromAmbientGeojson = (geojson, bbox) => {
  if (!geojson?.features?.length || !bbox) {
    return null
  }

  const {height, width} = getAmbientCanvasSize(bbox)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  context.clearRect(0, 0, width, height)

  geojson.features.forEach((feature) => {
    const color = feature.properties?.color
    const polygons = getAmbientFeaturePolygons(feature.geometry)

    if (!color || !polygons.length) {
      return
    }

    context.beginPath()
    polygons.forEach((polygon) => {
      polygon.forEach((ring) => {
        ring.forEach((coordinate, index) => {
          const point = getCanvasPointFromCoordinate(coordinate, bbox, width, height)

          if (!point) {
            return
          }

          if (index === 0) {
            context.moveTo(point.x, point.y)
            return
          }

          context.lineTo(point.x, point.y)
        })
        context.closePath()
      })
    })
    context.fillStyle = color
    context.fill('evenodd')
  })

  return canvas
}

const ComparisonModeButton = ({active, enabled, onClick, title}) => {
  return (
    <button
      type='button'
      disabled={!enabled}
      onClick={onClick}
      className={`view-land-comparison-mode-button ${active ? 'active' : ''}`.trim()}
    >
      {title}
    </button>
  )
}

const ComparisonSelect = ({className = '', label, onChange, options, placeholder = '-', value}) => {
  const [open, setOpen] = useState(false)
  const [menuMaxHeight, setMenuMaxHeight] = useState(null)
  const [menuPlacement, setMenuPlacement] = useState('down')
  const wrapperRef = useRef(null)
  const selectedOption = options.find((option) => option.value === value)

  const updateMenuLayout = useCallback(() => {
    const wrapper = wrapperRef.current

    if (!wrapper) {
      return
    }

    const wrapperRect = wrapper.getBoundingClientRect()
    let scrollParent = wrapper.parentElement

    while (scrollParent && scrollParent !== document.body) {
      const overflowY = window.getComputedStyle(scrollParent).overflowY

      if (overflowY === 'auto' || overflowY === 'scroll') {
        break
      }

      scrollParent = scrollParent.parentElement
    }

    const boundaryRect =
      scrollParent && scrollParent !== document.body
        ? scrollParent.getBoundingClientRect()
        : {top: 0, bottom: window.innerHeight}
    const menuGap = 3
    const maxMenuHeight = 238
    const minUsefulMenuHeight = 96
    const spaceBelow = Math.max(0, boundaryRect.bottom - wrapperRect.bottom - menuGap)
    const spaceAbove = Math.max(0, wrapperRect.top - boundaryRect.top - menuGap)
    const nextPlacement = spaceBelow < maxMenuHeight && spaceAbove > spaceBelow ? 'up' : 'down'
    const availableSpace = nextPlacement === 'up' ? spaceAbove : spaceBelow
    const nextMaxHeight = Math.max(
      minUsefulMenuHeight,
      Math.min(maxMenuHeight, Math.floor(availableSpace))
    )

    setMenuPlacement((current) => (current === nextPlacement ? current : nextPlacement))
    setMenuMaxHeight((current) => (current === nextMaxHeight ? current : nextMaxHeight))
  }, [])

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false)
      }
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const handleLayoutChange = () => updateMenuLayout()
    const animationFrame = window.requestAnimationFrame(updateMenuLayout)

    window.addEventListener('resize', handleLayoutChange)
    window.addEventListener('scroll', handleLayoutChange, true)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      window.removeEventListener('resize', handleLayoutChange)
      window.removeEventListener('scroll', handleLayoutChange, true)
    }
  }, [open, updateMenuLayout])

  const handleSelect = (option) => {
    if (option.disabled) {
      return
    }

    onChange({target: {value: option.value}})
    setOpen(false)
  }

  return (
    <div
      className={[
        'view-land-comparison-field',
        'view-land-comparison-select',
        open ? 'open' : '',
        open ? `menu-${menuPlacement}` : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      ref={wrapperRef}
    >
      <span className='view-land-comparison-field__label'>{label}</span>
      <button
        type='button'
        className='view-land-comparison-field__control view-land-comparison-select__button'
        aria-expanded={open}
        aria-haspopup='listbox'
        onClick={() => {
          if (!open) {
            updateMenuLayout()
          }

          setOpen((current) => !current)
        }}
      >
        <span
          className={`view-land-comparison-select__value ${
            selectedOption ? '' : 'is-placeholder'
          }`.trim()}
        >
          {selectedOption?.label || placeholder}
        </span>
        <span className='view-land-comparison-select__chevron' />
      </button>

      {open && (
        <div
          className='view-land-comparison-select__menu'
          role='listbox'
          style={menuMaxHeight ? {maxHeight: `${menuMaxHeight}px`} : undefined}
        >
          {options.map((option) => (
            <button
              type='button'
              role='option'
              aria-selected={option.value === value}
              disabled={option.disabled}
              className={`view-land-comparison-select__option ${
                option.value === value ? 'active' : ''
              }`.trim()}
              key={option.value}
              onClick={() => handleSelect(option)}
            >
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const ComparisonDatePicker = ({
  autoOpenSignal = 0,
  clearDates,
  cloudinessFilter,
  cloudyDates,
  disabled = false,
  label,
  onChange,
  placeholder = '-',
  value,
}) => {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const clearDateSet = useMemo(() => new Set(clearDates || []), [clearDates])
  const cloudyDateSet = useMemo(() => new Set(cloudyDates || []), [cloudyDates])
  const availableDates = useMemo(() => {
    return Array.from(new Set([...(clearDates || []), ...(cloudyDates || [])]))
  }, [clearDates, cloudyDates])
  const pickerValue = value
    ? new Date(`${value}T00:00:00`)
    : availableDates[0]
      ? new Date(`${availableDates[0]}T00:00:00`)
      : new Date()
  const isDisabled = disabled

  useEffect(() => {
    if (!autoOpenSignal || isDisabled || availableDates.length < 1) {
      return
    }

    setOpen(true)
  }, [autoOpenSignal, availableDates.length, isDisabled])

  const renderCalendarDay = (day, pickerSelectedDate, dayInCurrentMonth, dayComponent) => {
    const dateValue = format(day, 'yyyy-MM-dd')
    const isClear = clearDateSet.has(dateValue)
    // A day may contain both clear and cloudy acquisitions. Match Capas:
    // show green when at least one usable clear acquisition exists.
    const isCloudy = !isClear && cloudyDateSet.has(dateValue)
    const isActive = Boolean(value) && normalizeDateValue(pickerSelectedDate) === dateValue
    const tooltipMessage = isClear
      ? t('lands:view.comparison.fields.cloudiness_clear', {value: cloudinessFilter})
      : isCloudy
        ? t('lands:view.comparison.fields.cloudiness_cloudy', {value: cloudinessFilter})
        : t('lands:view.comparison.fields.no_date_data')

    return (
      <div
        className={[
          'view-land-comparison-calendar-day',
          dayInCurrentMonth ? '' : 'is-outside-month',
          isClear ? 'is-clear' : '',
          isCloudy ? 'is-cloudy' : '',
          isActive ? 'active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        title={tooltipMessage}
      >
        {dayComponent}
      </div>
    )
  }

  const shouldDisableCalendarDate = (date) => {
    const dateValue = format(date, 'yyyy-MM-dd')

    return !clearDateSet.has(dateValue) && !cloudyDateSet.has(dateValue)
  }

  return (
    <div
      className={`view-land-comparison-field view-land-comparison-date ${
        open ? 'open' : ''
      }`.trim()}
    >
      <span className='view-land-comparison-field__label'>{label}</span>
      <button
        type='button'
        className='view-land-comparison-field__control view-land-comparison-date__button'
        aria-expanded={open}
        disabled={isDisabled}
        onClick={() => setOpen(true)}
      >
        <span
          className={`view-land-comparison-select__value ${
            value ? '' : 'is-placeholder'
          }`.trim()}
        >
          {value ? toDateLabel(value) : placeholder}
        </span>
        <span className='view-land-comparison-select__chevron' />
      </button>
      <DatePicker
        open={open}
        value={pickerValue}
        autoOk
        okLabel={null}
        cancelLabel={t('common:actions.cancel')}
        renderDay={renderCalendarDay}
        shouldDisableDate={shouldDisableCalendarDate}
        onChange={(date) => {
          const nextValue = normalizeDateValue(date)

          if (nextValue) {
            onChange(nextValue)
          }

          setOpen(false)
        }}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        animateYearScrolling
        DialogProps={{
          maxWidth: false,
          PaperProps: {
            className: 'view-land-comparison-date-dialog',
          },
        }}
        TextFieldComponent={() => null}
      />
    </div>
  )
}

const ComparisonCloudinessControl = ({ariaLabel, className = '', label, onChange, value}) => {
  const handleChange = (event) => {
    onChange(clampCloudinessFilter(event.target.value))
  }

  return (
    <label className={`view-land-comparison-field view-land-comparison-cloudiness ${className}`.trim()}>
      {label && <span className='view-land-comparison-field__label'>{label}</span>}
      <span className='view-land-comparison-cloudiness__control'>
        <KTSVG
          path='/media/icons/duotune/files/fil020.svg'
          className='svg-icon svg-icon-2x svg-icon-primary view-land-comparison-cloudiness__icon'
        />
        <span className='view-land-comparison-cloudiness__prefix'>&le;</span>
        <input
          type='number'
          min='0'
          max='100'
          value={value}
          onChange={handleChange}
          aria-label={ariaLabel || label}
          className='view-land-comparison-cloudiness__input'
        />
        <span className='view-land-comparison-cloudiness__suffix'>%</span>
      </span>
    </label>
  )
}

const ComparisonRange = ({label, max = 100, min = 0, onChange, step = 0.1, value}) => {
  const updateFromPointer = useCallback(
    (event) => {
      const rect = event.currentTarget.getBoundingClientRect()
      const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1)
      const nextValue = Math.round(min + ratio * (max - min))

      onChange({target: {value: nextValue}})
    },
    [max, min, onChange]
  )

  return (
    <label className='view-land-comparison-range'>
      <span className='view-land-comparison-field__label'>{label}</span>
      <input
        type='range'
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={onChange}
        onInput={onChange}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture?.(event.pointerId)
          updateFromPointer(event)
        }}
        onPointerMove={(event) => {
          if (event.buttons === 1) {
            updateFromPointer(event)
          }
        }}
        className='form-range view-land-comparison-range__input'
      />
    </label>
  )
}

const Comparison = () => {
  const {t} = useTranslation()
  const {errorHandler} = useErrorHandler()
  const mapLayers = useMapLayersV2()
  const mapLegendContext = useMapLegendContext()
  const viewLandContext = useViewLandContext()
  const viewAmbientContext = useViewAmbientContext()
  const viewImageContext = useViewImageContext()
  const {
    ensureLandDates,
    ensureLandHdAccess,
    ensureLandHdAcquisitions,
    ensureLandSoilMap,
    ensureLandYieldMaps,
    getHdAccessStatus,
    getLandHdAcquisitionsState,
    getLandSoilMapState,
    getLandYieldMapsState,
    prepareLandHdAcquisition,
    registerComparisonReset,
    requestHdAccess,
    setComparisonMapOverlayActive,
  } = viewImageContext
  const landOperations = useLandOperationSummary()
  const land = viewLandContext.land
  const landId = land?.land_id
  const landGeometryHash = land?.geometry_hash || land?.geometryHash || ''
  const comparisonEnhancedFlag = Boolean(mapLegendContext.comparisonEnhanced?.[landId])
  const [comparisonState, setComparisonState] = useState(() => ({
    landId,
    ...getPersistedComparisonState(landId),
  }))
  const currentComparisonState = useMemo(() => {
    if (comparisonState.landId === landId) {
      return comparisonState
    }

    return {
      landId,
      ...getPersistedComparisonState(landId),
    }
  }, [comparisonState, landId])
  const {
    selectedMode,
    imageType,
    compareImageType,
    baseProduct,
    compareProduct,
    baseDate,
    compareDate,
    baseAcquisitionId,
    compareAcquisitionId,
    baseCloudinessFilter,
    compareCloudinessFilter,
    splitPercent,
    imageOpacity,
  } = currentComparisonState
  const updateComparisonState = useCallback(
    (updater) => {
      setComparisonState((previousState) => {
        const baseState =
          previousState.landId === landId
            ? previousState
            : {
                landId,
                ...getPersistedComparisonState(landId),
              }
        const patch = typeof updater === 'function' ? updater(baseState) : updater
        const nextState = {...baseState, ...patch}

        persistComparisonState(landId, nextState)
        return nextState
      })
    },
    [landId]
  )
  const setComparisonValue = useCallback(
    (key, valueOrUpdater) => {
      updateComparisonState((state) => ({
        [key]:
          typeof valueOrUpdater === 'function'
            ? valueOrUpdater(state[key])
            : valueOrUpdater,
      }))
    },
    [updateComparisonState]
  )
  const setSelectedMode = useCallback(
    (value) => setComparisonValue('selectedMode', value),
    [setComparisonValue]
  )
  const setImageType = useCallback(
    (value) =>
      updateComparisonState(() => ({
        imageType: value,
        baseAcquisitionId: '',
        baseItemId: '',
        ...(isLayerWithoutDate(value) ? {baseDate: ''} : {}),
      })),
    [updateComparisonState]
  )
  const setCompareImageType = useCallback(
    (value) =>
      updateComparisonState(() => ({
        compareImageType: value,
        compareAcquisitionId: '',
        compareItemId: '',
        ...(isLayerWithoutDate(value) ? {compareDate: ''} : {}),
      })),
    [updateComparisonState]
  )
  const setBaseDate = useCallback(
    (value) => updateComparisonState(() => ({
      baseDate: value,
      baseAcquisitionId: '',
      baseItemId: '',
    })),
    [updateComparisonState]
  )
  const setCompareDate = useCallback(
    (value) => updateComparisonState(() => ({
      compareDate: value,
      compareAcquisitionId: '',
      compareItemId: '',
    })),
    [updateComparisonState]
  )
  const applyComparisonProduct = useCallback((side, product) => {
    updateComparisonState((state) => {
      const isBase = side === DATE_PICKER_TARGET_BASE
      const layerKey = isBase ? 'imageType' : 'compareImageType'
      const dateKey = isBase ? 'baseDate' : 'compareDate'
      const productKey = isBase ? 'baseProduct' : 'compareProduct'
      const acquisitionKey = isBase ? 'baseAcquisitionId' : 'compareAcquisitionId'
      const itemKey = isBase ? 'baseItemId' : 'compareItemId'
      const currentLayer = state[layerKey]
      const nextImageTypes = getComparisonImageTypes(product)
      const nextLayer = isImageLayerType(currentLayer) && !nextImageTypes.includes(currentLayer)
        ? nextImageTypes[0]
        : currentLayer

      return {
        [productKey]: product,
        [layerKey]: nextLayer,
        [dateKey]: '',
        [acquisitionKey]: '',
        [itemKey]: '',
      }
    })
  }, [updateComparisonState])
  const setBaseCloudinessFilter = useCallback(
    (value) => setComparisonValue('baseCloudinessFilter', value),
    [setComparisonValue]
  )
  const setCompareCloudinessFilter = useCallback(
    (value) => setComparisonValue('compareCloudinessFilter', value),
    [setComparisonValue]
  )
  const setSplitPercent = useCallback(
    (value) => setComparisonValue('splitPercent', value),
    [setComparisonValue]
  )
  const setImageOpacity = useCallback(
    (value) => setComparisonValue('imageOpacity', value),
    [setComparisonValue]
  )
  const [debouncedBaseCloudinessFilter, setDebouncedBaseCloudinessFilter] = useState(
    baseCloudinessFilter
  )
  const [debouncedCompareCloudinessFilter, setDebouncedCompareCloudinessFilter] = useState(
    compareCloudinessFilter
  )
  const [baseSentinelDates, setBaseSentinelDates] = useState({
    clearDates: [],
    cloudyDatesList: [],
    status: 'idle',
  })
  const [compareSentinelDates, setCompareSentinelDates] = useState({
    clearDates: [],
    cloudyDatesList: [],
    status: 'idle',
  })
  const [status, setStatus] = useState('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [isDraggingDivider, setIsDraggingDivider] = useState(false)
  const [mapViewRevision, setMapViewRevision] = useState(0)
  const [baseDateAutoOpenSignal, setBaseDateAutoOpenSignal] = useState(0)
  const [compareDateAutoOpenSignal, setCompareDateAutoOpenSignal] = useState(0)
  const [pendingDatePicker, setPendingDatePicker] = useState(null)
  const [showDatePickerLoader, setShowDatePickerLoader] = useState(false)
  const [selectingHdProduct, setSelectingHdProduct] = useState(null)
  const [preparingHdAcquisition, setPreparingHdAcquisition] = useState(null)
  const imageComparisonSource = useRef(null)
  const requestId = useRef(0)
  const renderRequestId = useRef(0)
  const objectUrls = useRef([])
  const splitPercentRef = useRef(splitPercent)
  const errorHandlerRef = useRef(errorHandler)
  const mapLayersRef = useRef(mapLayers)
  const mapLegendContextRef = useRef(mapLegendContext)
  const tRef = useRef(t)
  const landPolygonsRef = useRef(null)
  const imageOpacityRef = useRef(imageOpacity)
  const ambientDataRef = useRef(null)
  const imageAmbientDataRef = useRef(null)
  const ambientHdDataRef = useRef(null)
  const imageAmbientHdDataRef = useRef(null)
  const baseDatesRequestId = useRef(0)
  const compareDatesRequestId = useRef(0)
  const previousComparisonLandId = useRef(landId)

  const soilMapState = getLandSoilMapState(landId)
  const yieldMapsState = getLandYieldMapsState(landId)
  const baseClearDates = useMemo(
    () => baseSentinelDates.clearDates || [],
    [baseSentinelDates.clearDates]
  )
  const baseCloudyDates = useMemo(
    () => baseSentinelDates.cloudyDatesList || [],
    [baseSentinelDates.cloudyDatesList]
  )
  const compareClearDates = useMemo(
    () => compareSentinelDates.clearDates || [],
    [compareSentinelDates.clearDates]
  )
  const compareCloudyDates = useMemo(
    () => compareSentinelDates.cloudyDatesList || [],
    [compareSentinelDates.cloudyDatesList]
  )
  const baseAvailableDates = useMemo(() => {
    return Array.from(
      new Set([
        ...baseClearDates,
        ...baseCloudyDates,
      ])
    ).sort((left, right) => new Date(`${right}T00:00:00`) - new Date(`${left}T00:00:00`))
  }, [baseClearDates, baseCloudyDates])
  const compareAvailableDates = useMemo(() => {
    return Array.from(
      new Set([
        ...compareClearDates,
        ...compareCloudyDates,
      ])
    ).sort((left, right) => new Date(`${right}T00:00:00`) - new Date(`${left}T00:00:00`))
  }, [compareClearDates, compareCloudyDates])
  const hdAccessStatus = getHdAccessStatus(landId)
  const baseSource = useMemo(
    () => getComparisonSource(baseProduct, imageType || 'rgb'),
    [baseProduct, imageType]
  )
  const compareSource = useMemo(
    () => getComparisonSource(compareProduct, compareImageType || 'rgb'),
    [compareImageType, compareProduct]
  )
  const baseHdAcquisitionsState = getLandHdAcquisitionsState(
    land,
    baseSource,
    null
  )
  const compareHdAcquisitionsState = getLandHdAcquisitionsState(
    land,
    compareSource,
    null
  )
  const baseHdItems = useMemo(
    () => selectBestPlanetHdItemsByDate(baseHdAcquisitionsState.items || []),
    [baseHdAcquisitionsState.items]
  )
  const compareHdItems = useMemo(
    () => selectBestPlanetHdItemsByDate(compareHdAcquisitionsState.items || []),
    [compareHdAcquisitionsState.items]
  )
  const selectedBaseHdItem = baseHdItems.find(
    (item) => item.acquisitionId === baseAcquisitionId
  ) || null
  const selectedCompareHdItem = compareHdItems.find(
    (item) => item.acquisitionId === compareAcquisitionId
  ) || null
  const baseHdDateHasMessage = Boolean(
    selectedBaseHdItem && [
      REMOTE_SENSING_DATE_STATES.REJECTED,
      REMOTE_SENSING_DATE_STATES.FAILED,
    ].includes(selectedBaseHdItem.status)
  )
  const compareHdDateHasMessage = Boolean(
    selectedCompareHdItem && [
      REMOTE_SENSING_DATE_STATES.REJECTED,
      REMOTE_SENSING_DATE_STATES.FAILED,
    ].includes(selectedCompareHdItem.status)
  )

  const hasHistoricalAmbient = landOperations.ambient.hasHistoricalAmbient
  const hasImageAmbient = landOperations.ambient.hasImageAmbient
  const hasHistoricalHdAmbient = PLANET_HD_V1_ENABLED && viewAmbientContext.canAccessHistoricalHd && landOperations.ambient.hasHistoricalHdAmbient
  const hasImageAmbientHd = PLANET_HD_V1_ENABLED && landOperations.ambient.hasImageAmbientHd
  const isBaseImageLayer = isImageLayerType(imageType)
  const isCompareImageLayer = isImageLayerType(compareImageType)
  const isBaseHdImageLayer = isBaseImageLayer && isPlanetHdComparisonProduct(baseProduct)
  const isCompareHdImageLayer = isCompareImageLayer && isPlanetHdComparisonProduct(compareProduct)
  const pendingDatePickerTargetsImageLayer =
    (pendingDatePicker === DATE_PICKER_TARGET_BASE && isBaseImageLayer && !isBaseHdImageLayer) ||
    (pendingDatePicker === DATE_PICKER_TARGET_COMPARE && isCompareImageLayer && !isCompareHdImageLayer)
  const pendingAvailableDates = pendingDatePicker === DATE_PICKER_TARGET_BASE
    ? baseAvailableDates
    : compareAvailableDates
  const pendingDatesStatus = pendingDatePicker === DATE_PICKER_TARGET_BASE
    ? baseSentinelDates.status
    : compareSentinelDates.status
  const isWaitingForDatePicker =
    Boolean(pendingDatePicker) &&
    pendingDatePickerTargetsImageLayer &&
    pendingAvailableDates.length < 1 &&
    (pendingDatesStatus === 'idle' || pendingDatesStatus === 'loading')
  const hasBaseLayerConfig = Boolean(
    imageType && (
      !isBaseImageLayer ||
      (isBaseHdImageLayer
        ? hdAccessStatus.enabled &&
          baseAcquisitionId &&
          isRemoteSensingDateVisualizable(selectedBaseHdItem)
        : baseDate)
    )
  )
  const hasCompareLayerConfig = Boolean(
    compareImageType && (
      !isCompareImageLayer ||
      (isCompareHdImageLayer
        ? hdAccessStatus.enabled &&
          compareAcquisitionId &&
          isRemoteSensingDateVisualizable(selectedCompareHdItem)
        : compareDate)
    )
  )
  const isSameLayerSelection = Boolean(
    imageType &&
      compareImageType &&
      imageType === compareImageType &&
      (!isImageLayerType(imageType) || (
        baseProduct === compareProduct &&
        (isBaseHdImageLayer
          ? baseAcquisitionId === compareAcquisitionId
          : baseDate === compareDate)
      ))
  )
  const hasCurrentComparisonConfig = Boolean(
    hasBaseLayerConfig && hasCompareLayerConfig && !isSameLayerSelection
  )
  const baseLayerId = getLayerId(IMAGE_COMPARE_LAYER_BASE, landId)
  const overlayLayerId = getLayerId(IMAGE_COMPARE_LAYER_OVERLAY, landId)
  const dividerLayerId = getLayerId(IMAGE_COMPARE_LAYER_DIVIDER, landId)
  const imageAmbientLayerId = getLayerId(IMAGE_AMBIENT_LAYER, landId)
  const ambientLayerId = getLayerId(AMBIENT_COMPARE_LAYER, landId)
  const altimetryLayerId = getLayerId(ALTIMETRY_COMPARE_LAYER, landId)

  const getLayerLabel = useCallback(
    (layerType) => {
      if (!layerType) {
        return ''
      }

      if (layerType === LAYER_TYPE_AMBIENT) {
        return t('lands:view.comparison.fields.historical_ambient')
      }

      if (layerType === LAYER_TYPE_IMAGE_AMBIENT) {
        return t('lands:view.comparison.fields.image_ambient')
      }

      if (layerType === LAYER_TYPE_AMBIENT_HD) {
        return t('lands:view.comparison.fields.historical_ambient_hd')
      }

      if (layerType === LAYER_TYPE_IMAGE_AMBIENT_HD) {
        return t('lands:view.comparison.fields.image_ambient_hd')
      }

      if (layerType === LAYER_TYPE_YIELD_MAP) {
        return t('lands:view.comparison.fields.yield_map', {
          defaultValue: 'Mapa de rendimiento',
        })
      }

      if (isImageLayerType(layerType)) {
        return t(`images:layer.${getRemoteLayer(layerType)}`, {
          defaultValue: getRemoteLayer(layerType).toUpperCase(),
        })
      }

      return t(`images:selector.${getImageTypeLabelKey(layerType)}`)
    },
    [t]
  )

  const getLayerSelectionLabel = useCallback(
    (layerType, dateValue, product = REMOTE_SENSING_PRODUCTS.SENTINEL) => {
      const label = getLayerLabel(layerType)

      if (!label) {
        return ''
      }

      if (!isImageLayerType(layerType)) {
        return label
      }

      const productLabel = isPlanetHdComparisonProduct(product)
        ? t('images:product.planet_hd', {defaultValue: 'Planet HD'})
        : t('images:product.sentinel', {defaultValue: 'Sentinel'})

      return dateValue
        ? `${productLabel} · ${label} - ${toDateLabel(dateValue)}`
        : `${productLabel} · ${label}`
    },
    [getLayerLabel, t]
  )

  const getLayerOption = useCallback(
    (layerType, product = null) => ({
      value: getComparisonLayerSelectValue(product, layerType),
      label: product && isImageLayerType(layerType)
        ? `${isPlanetHdComparisonProduct(product)
          ? t('images:product.planet_hd', {defaultValue: 'Planet HD'})
          : t('images:product.sentinel', {defaultValue: 'Sentinel'})} · ${getLayerLabel(layerType)}`
        : getLayerLabel(layerType),
      disabled: isComparisonHdProductDisabled({
        accessEnabled: hdAccessStatus.enabled,
        isHdProduct: isPlanetHdComparisonProduct(product),
        selectionPending: Boolean(selectingHdProduct),
      }) || isComparisonLayerDisabled(layerType, {
        hasHistoricalAmbient,
        hasImageAmbient,
        hasHistoricalHdAmbient,
        hasImageAmbientHd,
        soilMapStatus: soilMapState.status,
        yieldMapStatus: yieldMapsState.loading
          ? 'loading'
          : yieldMapsState.yieldMaps.length > 0 ? 'available' : 'empty',
      }),
    }),
    [
      getLayerLabel,
      hasHistoricalAmbient,
      hasHistoricalHdAmbient,
      hasImageAmbient,
      hasImageAmbientHd,
      hdAccessStatus.enabled,
      selectingHdProduct,
      soilMapState.status,
      t,
      yieldMapsState.loading,
      yieldMapsState.yieldMaps.length,
    ]
  )
  const remoteImageLayerOptions = useMemo(
    () => [
      ...getComparisonImageTypes(REMOTE_SENSING_PRODUCTS.SENTINEL).map((layerType) =>
        getLayerOption(layerType, REMOTE_SENSING_PRODUCTS.SENTINEL)
      ),
      ...(PLANET_HD_V1_ENABLED
        ? getComparisonImageTypes(REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD).map((layerType) =>
            getLayerOption(layerType, REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD)
          )
        : []),
    ],
    [getLayerOption]
  )

  const baseLayerOptions = useMemo(
    () => [
      GOOGLE_MAP_IMAGE_TYPE,
      ...remoteImageLayerOptions,
      ...BASE_NON_IMAGE_LAYER_OPTIONS.filter((option) => option !== GOOGLE_MAP_IMAGE_TYPE),
      ...(PLANET_HD_V1_ENABLED
        ? [...(viewAmbientContext.canAccessHistoricalHd ? [LAYER_TYPE_AMBIENT_HD] : []), LAYER_TYPE_IMAGE_AMBIENT_HD]
        : []),
    ].map((option) => typeof option === 'string' ? getLayerOption(option) : option),
    [getLayerOption, remoteImageLayerOptions, viewAmbientContext.canAccessHistoricalHd]
  )
  const compareLayerOptions = useMemo(
    () => [
      GOOGLE_MAP_IMAGE_TYPE,
      ...remoteImageLayerOptions,
      ...COMPARE_NON_IMAGE_LAYER_OPTIONS.filter((option) => option !== GOOGLE_MAP_IMAGE_TYPE),
      ...(PLANET_HD_V1_ENABLED
        ? [...(viewAmbientContext.canAccessHistoricalHd ? [LAYER_TYPE_AMBIENT_HD] : []), LAYER_TYPE_IMAGE_AMBIENT_HD]
        : []),
    ].map((option) => typeof option === 'string' ? getLayerOption(option) : option),
    [getLayerOption, remoteImageLayerOptions, viewAmbientContext.canAccessHistoricalHd]
  )
  const comparisonMethods = [
    {
      id: COMPARISON_METHOD_SWIPE,
      title: t('lands:view.comparison.methods.swipe'),
      enabled: true,
    },
    {
      id: COMPARISON_METHOD_OPACITY,
      title: t('lands:view.comparison.methods.opacity'),
      enabled: true,
    },
  ]
  const handleComparisonProductChange = useCallback(async (side, nextProduct) => {
    applyComparisonProduct(side, nextProduct)
    if (!isPlanetHdComparisonProduct(nextProduct) || !land || hdAccessStatus.enabled) {
      return
    }

    setSelectingHdProduct(side)
    try {
      await requestHdAccess({
        land,
        operation: 'comparison_planet_hd',
        context: {comparison_side: side},
        onConfirmed: () => true,
      })
    } finally {
      setSelectingHdProduct(null)
    }
  }, [applyComparisonProduct, hdAccessStatus.enabled, land, requestHdAccess])

  const handleHdAcquisitionChange = useCallback(async (side, acquisitionId) => {
    const isBase = side === DATE_PICKER_TARGET_BASE
    const items = isBase ? baseHdItems : compareHdItems
    const source = isBase ? baseSource : compareSource
    const item = items.find((candidate) => candidate.acquisitionId === acquisitionId)

    if (!item || !land || preparingHdAcquisition) {
      return
    }

    // Keep the chosen date visible while an on-demand acquisition is prepared.
    // Rendering still requires a READY/REJECTED_QUALITY item below. Failed
    // acquisitions stay selected so the picker can explain the error, exactly
    // like Capas, without retrying or sending a provider request.
    updateComparisonState(() => isBase
      ? {
          baseAcquisitionId: item.acquisitionId,
          baseItemId: item.itemId || '',
          baseDate: item.date,
        }
      : {
          compareAcquisitionId: item.acquisitionId,
          compareItemId: item.itemId || '',
          compareDate: item.date,
        })

    if (!canPrepareRemoteSensingDate(item)) {
      return
    }

    setPreparingHdAcquisition(`${side}:${item.acquisitionId}`)
    try {
      await prepareLandHdAcquisition(land, item, source)
      await ensureLandHdAcquisitions(
        land,
        source,
        null,
        {force: true}
      )
    } finally {
      setPreparingHdAcquisition(null)
    }
  }, [
    baseHdItems,
    baseSource,
    compareHdItems,
    compareSource,
    ensureLandHdAcquisitions,
    land,
    prepareLandHdAcquisition,
    preparingHdAcquisition,
    updateComparisonState,
  ])

  const requestDatePickerOpen = useCallback(
    (target) => {
      if (!landId) {
        setPendingDatePicker(null)
        return
      }

      setPendingDatePicker(target)
      const threshold = target === DATE_PICKER_TARGET_BASE
        ? debouncedBaseCloudinessFilter
        : debouncedCompareCloudinessFilter
      ensureLandDates(landId, threshold).catch(() => {
        setPendingDatePicker((currentTarget) =>
          currentTarget === target ? null : currentTarget
        )
      })
    },
    [
      debouncedBaseCloudinessFilter,
      debouncedCompareCloudinessFilter,
      ensureLandDates,
      landId,
    ]
  )

  const handleBaseLayerChange = useCallback(
    (event) => {
      const {product, layerType: nextLayerType} = parseComparisonLayerSelectValue(event.target.value)

      if (product) {
        handleComparisonProductChange(DATE_PICKER_TARGET_BASE, product)
      }
      setImageType(nextLayerType)

      if (isImageLayerType(nextLayerType) && !isPlanetHdComparisonProduct(product)) {
        requestDatePickerOpen(DATE_PICKER_TARGET_BASE)
      }
    },
    [
      handleComparisonProductChange,
      requestDatePickerOpen,
      setImageType,
    ]
  )

  const handleCompareLayerChange = useCallback(
    (event) => {
      const {product, layerType: nextLayerType} = parseComparisonLayerSelectValue(event.target.value)

      if (product) {
        handleComparisonProductChange(DATE_PICKER_TARGET_COMPARE, product)
      }
      setCompareImageType(nextLayerType)

      if (isImageLayerType(nextLayerType) && !isPlanetHdComparisonProduct(product)) {
        requestDatePickerOpen(DATE_PICKER_TARGET_COMPARE)
      }
    },
    [
      handleComparisonProductChange,
      requestDatePickerOpen,
      setCompareImageType,
    ]
  )

  const revokeObjectUrls = useCallback(() => {
    objectUrls.current.forEach((url) => URL.revokeObjectURL(url))
    objectUrls.current = []
  }, [])

  const releaseImageComparisonSource = useCallback(() => {
    disposeDrawableImage(imageComparisonSource.current?.overlayDrawable)
    imageComparisonSource.current = null
  }, [])

  const clearComparisonLayers = useCallback(({keepLegends = false} = {}) => {
    releaseImageComparisonSource()
    revokeObjectUrls()

    if (!landId) {
      return
    }

    if (!keepLegends) {
      mapLegendContextRef.current.clearComparisonLegends(landId)
    }

    if (!mapState.isInitialized()) {
      return
    }

    mapLayersRef.current.land.deleteImage({layerId: baseLayerId})
    mapLayersRef.current.land.deleteImage({layerId: overlayLayerId})
    mapLayersRef.current.land.deleteImage({layerId: imageAmbientLayerId})
    mapLayersRef.current.land.deleteImage({layerId: altimetryLayerId})
    mapLayersRef.current.land.deleteLayer({layerId: dividerLayerId})

    try {
      mapLayersRef.current.land.ambient.remove({landId, layerId: ambientLayerId})
    } catch (error) {
      // The comparison ambient layer is optional and may not exist yet during route changes.
    }
  }, [
    ambientLayerId,
    altimetryLayerId,
    baseLayerId,
    dividerLayerId,
    imageAmbientLayerId,
    landId,
    overlayLayerId,
    releaseImageComparisonSource,
    revokeObjectUrls,
  ])

  const resetComparisonOptions = useCallback(() => {
    requestId.current += 1
    renderRequestId.current += 1
    clearComparisonLayers()
    setStatus('idle')
    setErrorMessage('')
    setIsDraggingDivider(false)
    setMapViewRevision((revision) => revision + 1)
    setDebouncedBaseCloudinessFilter(DEFAULT_COMPARISON_STATE.baseCloudinessFilter)
    setDebouncedCompareCloudinessFilter(DEFAULT_COMPARISON_STATE.compareCloudinessFilter)
    mapLegendContextRef.current.disableComparisonEnhanced?.(landId)
    splitPercentRef.current = DEFAULT_COMPARISON_STATE.splitPercent
    imageOpacityRef.current = DEFAULT_COMPARISON_STATE.imageOpacity

    const nextState = {
      landId,
      ...DEFAULT_COMPARISON_STATE,
      selectedMode,
    }

    setComparisonState(nextState)
    persistComparisonState(landId, nextState)
    setComparisonMapOverlayActive(false)
  }, [clearComparisonLayers, landId, selectedMode, setComparisonMapOverlayActive])

  useEffect(() => {
    return registerComparisonReset(resetComparisonOptions)
  }, [registerComparisonReset, resetComparisonOptions])

  useEffect(() => {
    setComparisonMapOverlayActive(
      viewLandContext.activeSection === 'comparison' && hasCurrentComparisonConfig
    )

    return () => {
      setComparisonMapOverlayActive(false)
    }
  }, [
    hasCurrentComparisonConfig,
    setComparisonMapOverlayActive,
    viewLandContext.activeSection,
  ])

  useEffect(() => {
    if (previousComparisonLandId.current === landId) {
      return
    }

    previousComparisonLandId.current = landId
    requestId.current += 1
    renderRequestId.current += 1
    setStatus('idle')
    setErrorMessage('')
    setIsDraggingDivider(false)
    const persisted = getPersistedComparisonState(landId)
    setDebouncedBaseCloudinessFilter(persisted.baseCloudinessFilter)
    setDebouncedCompareCloudinessFilter(persisted.compareCloudinessFilter)
  }, [landId])

  const fetchImageLayer = useCallback(
    async ({acquisition = null, dateValue, product, type}) => {
      if (isPlanetHdComparisonProduct(product) && (
        !acquisition?.acquisitionId || !hdAccessStatus.enabled ||
        !doesPlanetHdSelectionMatchAccess(acquisition, hdAccessStatus.access)
      )) {
        throw new Error('comparison-hd-acquisition-not-ready')
      }

      const layer = getRemoteLayer(type)
      const source = getComparisonSource(product, type)
      const imageType = getImageTypeForSource(source)
      const enhanced = shouldEnhanceComparisonImageLayer(type, comparisonEnhancedFlag)
      const normalizedAcquisition = acquisition
        ? {
            ...acquisition,
            accessId: acquisition.accessId || hdAccessStatus.access?.access_id || null,
            source,
          }
        : null
      const cacheOptions = buildImageLayerCacheOptions({
        acquisition: normalizedAcquisition,
        date: dateValue,
        enhanced,
        imageType,
        land,
      })
      const cachedLayer = viewImageState.getLandImageLayer(landId, cacheOptions)

      if (cachedLayer) {
        if (!cachedLayer.legend) {
          cachedLayer.legend = normalizeComparisonImageLegend(cachedLayer.headers)
        }

        return cachedLayer
      }

      const cancelTokenRequest = axios.CancelToken.source()
      const res = await errorHandlerRef.current(
        requestRemoteSensingLayer({
          acquisition: normalizedAcquisition,
          cancelTokenRequest,
          imageType,
          land,
          layer,
          options: {date: dateValue, enhanced},
        })
      )

      if (!res?.ok) {
        throw new Error('comparison-layer-request-failed')
      }

      const payload = {
        blob: res.data,
        bbox: getLayerBbox(res.headers, land?.polygons),
        headers: res.headers,
        legend: normalizeComparisonImageLegend(res.headers),
      }

      if (!payload.bbox) {
        throw new Error('comparison-layer-missing-bbox')
      }

      viewImageState.addLandImageLayer(landId, cacheOptions, payload)

      return payload
    },
    [comparisonEnhancedFlag, hdAccessStatus.access, hdAccessStatus.enabled, land, landId]
  )

  const dividerGuideGeometry = useMemo(() => {
    const source = imageComparisonSource.current

    if (!Number.isFinite(mapViewRevision)) {
      return null
    }

    if (
      selectedMode !== COMPARISON_METHOD_SWIPE ||
      status !== 'ready' ||
      !source?.overlay?.bbox
    ) {
      return null
    }

    return getDividerGuideGeometry(source.overlay.bbox, splitPercent)
  }, [mapViewRevision, selectedMode, splitPercent, status])

  const renderImageSwipe = useCallback(
    async (source = imageComparisonSource.current, split = splitPercentRef.current) => {
      const polygons = landPolygonsRef.current

      if (!landId || !source || !polygons) {
        return
      }

      const overlayCanvas = paintSwipeCanvas(source, split)

      if (!overlayCanvas) {
        return
      }

      mapLayersRef.current.land.addCanvas({
        layerId: overlayLayerId,
        canvas: overlayCanvas,
        bbox: source.overlay.bbox,
        opacity: 1,
      })
    },
    [landId, overlayLayerId]
  )

  const updateSplitFromPointer = useCallback(
    (event) => {
      const source = imageComparisonSource.current

      if (!source?.overlay?.bbox) {
        return
      }

      const nextSplitPercent = getSplitPercentFromPointer({
        bbox: source.overlay.bbox,
        clientX: event.clientX,
        clientY: event.clientY,
      })

      if (nextSplitPercent != null) {
        splitPercentRef.current = nextSplitPercent
        setSplitPercent(nextSplitPercent)
      }
    },
    [setSplitPercent]
  )

  const loadComparisonLayerAsset = useCallback(
    async ({acquisition, bboxHint, dateValue, layerType, polygons, product}) => {
      const label = isImageLayerType(layerType)
        ? getLayerSelectionLabel(layerType, dateValue, product)
        : getLayerLabel(layerType)

      if (layerType === GOOGLE_MAP_IMAGE_TYPE) {
        return {
          bbox: bboxHint,
          isGoogleMap: true,
          label,
          layerType,
        }
      }

      if (isImageLayerType(layerType)) {
        const layer = await fetchImageLayer({acquisition, dateValue, product, type: layerType})
        const drawable = await createDrawableImage(layer.blob)
        const canvas = createCanvasFromDrawable(drawable)
        disposeDrawableImage(drawable)

        return {
          bbox: layer.bbox,
          canvas,
          label,
          legend: layer.legend || null,
          legendType: layer.legend ? 'common' : null,
          isEnhanceable: isEnhanceableImageLayer(layerType),
          layerType,
        }
      }

      if (layerType === LAYER_TYPE_ALTIMETRY) {
        const geometryKey = JSON.stringify(polygons)
        const cachedAltimetryRaster = viewImageState.getLandAltimetry(landId, geometryKey)
        const raster =
          cachedAltimetryRaster ||
          (await buildAltimetryRaster(polygons).then((nextRaster) => {
            viewImageState.addLandAltimetry(landId, nextRaster, geometryKey)
            return nextRaster
          }))

        return {
          bbox: raster.bbox,
          canvas: raster.canvas,
          label,
          legend: raster.legend,
          legendType: 'common',
          layerType,
        }
      }

      if (layerType === LAYER_TYPE_SOIL_MAP) {
        const soilMap = await ensureLandSoilMap(landId)
        const featureCollection = soilMap?.featureCollection
        const bbox = getSoilMapBbox(featureCollection) || bboxHint
        const canvas = createSoilMapCanvas(featureCollection, bbox)

        if (soilMap?.status !== 'available' || !canvas || !bbox) {
          throw new Error('comparison-soil-map-unavailable')
        }

        return {
          bbox,
          canvas,
          label,
          legend: getSoilMapLegendModel(soilMap),
          legendType: 'common',
          layerType,
        }
      }

      if (layerType === LAYER_TYPE_YIELD_MAP) {
        const yieldMaps = await ensureLandYieldMaps(landId)
        const yieldMap = yieldMaps[0]

        if (!yieldMap?.id) {
          throw new Error('comparison-yield-map-unavailable')
        }

        const [pngResponse, gridResponse] = await Promise.all([
          GisDataAdapter.downloadLandYieldMap(landId, yieldMap.id, {driver: 'PNG'}),
          GisDataAdapter.downloadLandYieldMap(landId, yieldMap.id, {driver: 'GRID'}),
        ])
        if (!pngResponse?.ok || !gridResponse?.ok) {
          throw new Error('comparison-yield-map-unavailable')
        }

        const grid = await parseBlobJson(gridResponse.data)
        const bbox = grid?.bbox
        if (!Array.isArray(bbox) || bbox.length !== 4) {
          throw new Error('comparison-yield-map-unavailable')
        }

        const drawable = await createDrawableImage(pngResponse.data)
        const canvas = createCanvasFromDrawable(drawable)
        disposeDrawableImage(drawable)

        return {
          bbox,
          canvas,
          label,
          legend: {...(grid.legend || {}), type: 'yield_map'},
          legendType: 'common',
          layerType,
        }
      }

      if (isAmbientLayerType(layerType)) {
        if (layerType === LAYER_TYPE_AMBIENT_HD && !viewAmbientContext.canAccessHistoricalHd) {
          throw new Error('planet_hd_historical_internal_only')
        }
        const ambient =
          layerType === LAYER_TYPE_IMAGE_AMBIENT_HD
            ? imageAmbientHdDataRef.current
            : layerType === LAYER_TYPE_AMBIENT_HD
              ? ambientHdDataRef.current
              : layerType === LAYER_TYPE_IMAGE_AMBIENT
                ? imageAmbientDataRef.current
                : ambientDataRef.current
        const paintedAmbient = paintAmbientGeojson(ambient)
        const bbox =
          bboxHint ||
          getPolygonImageBbox(
            paintedAmbient?.features?.map((feature) => feature.geometry?.coordinates)
          )
        const canvas = createCanvasFromAmbientGeojson(paintedAmbient, bbox)

        if (!canvas) {
          throw new Error('comparison-ambient-unavailable')
        }

        return {
          bbox,
          canvas,
          label,
          legend: ambient,
          legendType: 'ambient',
          layerType,
        }
      }

      throw new Error('comparison-unknown-layer')
    },
    [
      ensureLandSoilMap,
      ensureLandYieldMaps,
      fetchImageLayer,
      getLayerLabel,
      getLayerSelectionLabel,
      landId,
      viewAmbientContext.canAccessHistoricalHd,
    ]
  )

  const loadComparisonLayers = useCallback(async () => {
    const polygons = landPolygonsRef.current

    if (!landId || !polygons || !hasCurrentComparisonConfig) {
      setStatus('idle')
      return
    }

    const currentRequestId = ++requestId.current
    setStatus('loading')
    setErrorMessage('')
    clearComparisonLayers({keepLegends: true})

    try {
      await waitForMapInitialized()

      if (currentRequestId !== requestId.current) {
        return
      }

      const bboxHint =
        getPolygonImageBbox(getMapLandGeometry(landId)) ||
        getPolygonImageBbox(polygons)
      const [baseAsset, compareAsset] = await Promise.all([
        loadComparisonLayerAsset({
          acquisition: isBaseHdImageLayer ? selectedBaseHdItem : null,
          bboxHint,
          dateValue: baseDate,
          layerType: imageType,
          polygons,
          product: baseProduct,
        }),
        loadComparisonLayerAsset({
          acquisition: isCompareHdImageLayer ? selectedCompareHdItem : null,
          bboxHint,
          dateValue: compareDate,
          layerType: compareImageType,
          polygons,
          product: compareProduct,
        }),
      ])

      if (currentRequestId !== requestId.current) {
        return
      }

      const comparisonBbox = bboxHint || baseAsset?.bbox || compareAsset?.bbox

      if (!comparisonBbox) {
        throw new Error('comparison-missing-layer')
      }

      const normalizedBaseAsset = {
        ...baseAsset,
        bbox: comparisonBbox,
      }
      const normalizedCompareAsset = {
        ...compareAsset,
        bbox: comparisonBbox,
      }
      const comparisonLegends = getComparisonLegendItems(
        normalizedBaseAsset,
        normalizedCompareAsset
      )
      const composition = getComparisonMapComposition({
        baseAsset: normalizedBaseAsset,
        compareAsset: normalizedCompareAsset,
        method: selectedMode,
        compareOpacityPercent: imageOpacityRef.current,
      })

      if (!composition.isRenderable) {
        throw new Error('comparison-missing-layer')
      }

      mapLegendContextRef.current.changeComparisonLegends(landId, comparisonLegends)

      if (composition.backgroundAsset?.canvas) {
        mapLayersRef.current.land.addCanvas({
          layerId: baseLayerId,
          canvas: composition.backgroundAsset.canvas,
          bbox: comparisonBbox,
          opacity: 1,
        })
      }

      if (!composition.overlayAsset?.canvas) {
        imageComparisonSource.current = null
      } else if (selectedMode === COMPARISON_METHOD_SWIPE) {
        const source = {
          base: normalizedBaseAsset,
          overlay: composition.overlayAsset,
          imageType: compareImageType,
          overlayDrawable: composition.overlayAsset.canvas,
          overlayCanvas: null,
          swipeSide: composition.swipeSide,
        }

        imageComparisonSource.current = source
        await renderImageSwipe(source, splitPercentRef.current)
      } else {
        imageComparisonSource.current = {
          overlay: composition.overlayAsset,
          opacityInverted: normalizedCompareAsset.isGoogleMap,
        }
        mapLayersRef.current.land.addCanvas({
          layerId: overlayLayerId,
          canvas: composition.overlayAsset.canvas,
          bbox: comparisonBbox,
          opacity: composition.overlayOpacity,
        })
      }

      mapLayersRef.current.fit(landId)
      setStatus('ready')
      setMapViewRevision((revision) => revision + 1)
    } catch (error) {
      console.error(error)
      if (currentRequestId === requestId.current) {
        mapLegendContextRef.current.clearComparisonLegends(landId)
        setStatus('error')
        setErrorMessage(tRef.current('lands:view.comparison.errors.unified'))
      }
    }
  }, [
    baseDate,
    baseLayerId,
    baseProduct,
    clearComparisonLayers,
    compareDate,
    compareImageType,
    compareProduct,
    hasCurrentComparisonConfig,
    imageType,
    isBaseHdImageLayer,
    isCompareHdImageLayer,
    landId,
    loadComparisonLayerAsset,
    overlayLayerId,
    renderImageSwipe,
    selectedBaseHdItem,
    selectedCompareHdItem,
    selectedMode,
  ])

  useEffect(() => {
    splitPercentRef.current = splitPercent
  }, [splitPercent])

  useEffect(() => {
    imageOpacityRef.current = imageOpacity
  }, [imageOpacity])

  useEffect(() => {
    errorHandlerRef.current = errorHandler
  }, [errorHandler])

  useEffect(() => {
    mapLayersRef.current = mapLayers
  }, [mapLayers])

  useEffect(() => {
    mapLegendContextRef.current = mapLegendContext
  }, [mapLegendContext])

  useEffect(() => {
    tRef.current = t
  }, [t])

  useEffect(() => {
    const baseTimeoutId = window.setTimeout(() => {
      setDebouncedBaseCloudinessFilter(baseCloudinessFilter)
    }, 450)
    const compareTimeoutId = window.setTimeout(() => {
      setDebouncedCompareCloudinessFilter(compareCloudinessFilter)
    }, 450)

    return () => {
      window.clearTimeout(baseTimeoutId)
      window.clearTimeout(compareTimeoutId)
    }
  }, [baseCloudinessFilter, compareCloudinessFilter])

  useEffect(() => {
    if (landId) {
      ensureLandYieldMaps(landId).catch(() => {})
    }
  }, [ensureLandYieldMaps, landId])

  useEffect(() => {
    const hasHdSelection = isBaseHdImageLayer || isCompareHdImageLayer
    if (
      !hasHdSelection ||
      !landId ||
      hdAccessStatus.state !== PLANET_HD_ACCESS_STATES.PROVISIONING
    ) {
      return undefined
    }

    const intervalId = window.setInterval(() => {
      ensureLandHdAccess(landId, {
        force: true,
        geometryHash: landGeometryHash,
        operation: 'comparison_planet_hd',
      }).catch(() => {})
    }, 5000)

    return () => window.clearInterval(intervalId)
  }, [
    ensureLandHdAccess,
    hdAccessStatus.state,
    isBaseHdImageLayer,
    isCompareHdImageLayer,
    landGeometryHash,
    landId,
  ])

  useEffect(() => {
    if (!land || !hdAccessStatus.enabled) {
      return
    }

    if (isBaseHdImageLayer) {
      ensureLandHdAcquisitions(
        land,
        baseSource,
        null
      ).catch(() => {})
    }

    if (isCompareHdImageLayer) {
      ensureLandHdAcquisitions(
        land,
        compareSource,
        null
      ).catch(() => {})
    }
  }, [
    baseSource,
    compareSource,
    ensureLandHdAcquisitions,
    hdAccessStatus.enabled,
    isBaseHdImageLayer,
    isCompareHdImageLayer,
    land,
  ])

  useEffect(() => {
    const hasPreparingAcquisition = [
      ...(isBaseHdImageLayer ? baseHdItems : []),
      ...(isCompareHdImageLayer ? compareHdItems : []),
    ].some((item) => item.status === REMOTE_SENSING_DATE_STATES.PREPARING)

    if (!hasPreparingAcquisition || !land || !hdAccessStatus.enabled) {
      return undefined
    }

    const intervalId = window.setInterval(() => {
      if (isBaseHdImageLayer) {
        ensureLandHdAcquisitions(
          land,
          baseSource,
          null,
          {force: true}
        ).catch(() => {})
      }
      if (isCompareHdImageLayer) {
        ensureLandHdAcquisitions(
          land,
          compareSource,
          null,
          {force: true}
        ).catch(() => {})
      }
    }, 5000)

    return () => window.clearInterval(intervalId)
  }, [
    baseHdItems,
    baseSource,
    compareHdItems,
    compareSource,
    ensureLandHdAcquisitions,
    hdAccessStatus.enabled,
    isBaseHdImageLayer,
    isCompareHdImageLayer,
    land,
  ])

  useEffect(() => {
    if (!landId || !isBaseImageLayer || isBaseHdImageLayer) {
      return undefined
    }

    const currentRequestId = baseDatesRequestId.current + 1
    baseDatesRequestId.current = currentRequestId
    setBaseSentinelDates((current) => ({...current, status: 'loading'}))
    ensureLandDates(landId, debouncedBaseCloudinessFilter)
      .then((nextState) => {
        if (currentRequestId === baseDatesRequestId.current) {
          setBaseSentinelDates(nextState)
        }
      })
      .catch(() => {
        if (currentRequestId === baseDatesRequestId.current) {
          setBaseSentinelDates({clearDates: [], cloudyDatesList: [], status: 'error'})
        }
      })

    return undefined
  }, [
    debouncedBaseCloudinessFilter,
    ensureLandDates,
    isBaseHdImageLayer,
    isBaseImageLayer,
    landId,
  ])

  useEffect(() => {
    if (!landId || !isCompareImageLayer || isCompareHdImageLayer) {
      return undefined
    }

    const currentRequestId = compareDatesRequestId.current + 1
    compareDatesRequestId.current = currentRequestId
    setCompareSentinelDates((current) => ({...current, status: 'loading'}))
    ensureLandDates(landId, debouncedCompareCloudinessFilter)
      .then((nextState) => {
        if (currentRequestId === compareDatesRequestId.current) {
          setCompareSentinelDates(nextState)
        }
      })
      .catch(() => {
        if (currentRequestId === compareDatesRequestId.current) {
          setCompareSentinelDates({clearDates: [], cloudyDatesList: [], status: 'error'})
        }
      })

    return undefined
  }, [
    debouncedCompareCloudinessFilter,
    ensureLandDates,
    isCompareHdImageLayer,
    isCompareImageLayer,
    landId,
  ])

  useEffect(() => {
    if (!pendingDatePicker) {
      return
    }

    if (!pendingDatePickerTargetsImageLayer) {
      setPendingDatePicker(null)
      return
    }

    if (pendingAvailableDates.length > 0) {
      if (pendingDatePicker === DATE_PICKER_TARGET_BASE) {
        setBaseDateAutoOpenSignal((signal) => signal + 1)
      } else {
        setCompareDateAutoOpenSignal((signal) => signal + 1)
      }

      setPendingDatePicker(null)
      return
    }

    if (pendingDatesStatus === 'empty' || pendingDatesStatus === 'error') {
      setPendingDatePicker(null)
    }
  }, [
    pendingAvailableDates.length,
    pendingDatesStatus,
    pendingDatePicker,
    pendingDatePickerTargetsImageLayer,
  ])

  useEffect(() => {
    if (!isWaitingForDatePicker) {
      setShowDatePickerLoader(false)
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      setShowDatePickerLoader(true)
    }, 180)

    return () => {
      window.clearTimeout(timeoutId)
      setShowDatePickerLoader(false)
    }
  }, [isWaitingForDatePicker])

  useEffect(() => {
    landPolygonsRef.current = land?.polygons || null
  }, [land?.polygons])

  useEffect(() => {
    ambientDataRef.current = viewAmbientContext.ambientData
    imageAmbientDataRef.current = viewAmbientContext.imageAmbientData
    ambientHdDataRef.current = viewAmbientContext.ambientHdData
    imageAmbientHdDataRef.current = viewAmbientContext.imageAmbientHdData
  }, [
    viewAmbientContext.ambientData,
    viewAmbientContext.ambientHdData,
    viewAmbientContext.imageAmbientData,
    viewAmbientContext.imageAmbientHdData,
  ])

  useEffect(() => {
    if (!isBaseImageLayer && baseDate) {
      setBaseDate('')
    } else if (
      isBaseHdImageLayer &&
      baseAcquisitionId &&
      baseHdAcquisitionsState.status !== 'loading' &&
      !selectedBaseHdItem
    ) {
      setBaseDate('')
    } else if (
      !isBaseHdImageLayer &&
      baseDate &&
      baseAvailableDates.length > 0 &&
      !baseAvailableDates.includes(baseDate)
    ) {
      setBaseDate('')
    }

    if (!isCompareImageLayer && compareDate) {
      setCompareDate('')
    } else if (
      isCompareHdImageLayer &&
      compareAcquisitionId &&
      compareHdAcquisitionsState.status !== 'loading' &&
      !selectedCompareHdItem
    ) {
      setCompareDate('')
    } else if (
      !isCompareHdImageLayer &&
      compareDate &&
      compareAvailableDates.length > 0 &&
      !compareAvailableDates.includes(compareDate)
    ) {
      setCompareDate('')
    }
  }, [
    baseAvailableDates,
    baseAcquisitionId,
    baseDate,
    baseHdAcquisitionsState.status,
    compareAcquisitionId,
    compareDate,
    compareHdAcquisitionsState.status,
    compareAvailableDates,
    isBaseImageLayer,
    isBaseHdImageLayer,
    isCompareImageLayer,
    isCompareHdImageLayer,
    selectedBaseHdItem,
    selectedCompareHdItem,
    setBaseDate,
    setCompareDate,
  ])

  useEffect(() => {
    const activeImageType = landOperations.image.imageType
    const selectedImageDate = landOperations.image.selectedDate

    if (!landId || !activeImageType || !selectedImageDate) {
      return undefined
    }

    mapLayersRef.current.land.setImageVisibility({
      landId,
      imageType: getMapImageType(activeImageType),
      visible: false,
    })

    return () => {
      mapLayersRef.current.land.setImageVisibility({
        landId,
        imageType: getMapImageType(activeImageType),
        visible: true,
      })
    }
  }, [landId, landOperations.image.imageType, landOperations.image.selectedDate])

  useEffect(() => {
    if (!hasCurrentComparisonConfig) {
      requestId.current += 1
      clearComparisonLayers()
      setStatus('idle')
      setErrorMessage('')
      return undefined
    }

    imageComparisonSource.current = null
    const timeoutId = window.setTimeout(() => {
      loadComparisonLayers()
    }, 120)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [clearComparisonLayers, hasCurrentComparisonConfig, loadComparisonLayers])

  useEffect(() => {
    if (
      selectedMode !== COMPARISON_METHOD_SWIPE ||
      !imageComparisonSource.current
    ) {
      return
    }

    renderImageSwipe(imageComparisonSource.current, splitPercent)
  }, [renderImageSwipe, selectedMode, splitPercent])

  useEffect(() => {
    if (selectedMode !== COMPARISON_METHOD_SWIPE || status !== 'ready') {
      return undefined
    }

    const map = mapState.map

    if (!map) {
      return undefined
    }

    const update = () => setMapViewRevision((revision) => revision + 1)

    update()
    map.on('move', update)
    map.on('zoom', update)
    map.on('resize', update)
    window.addEventListener('resize', update)

    return () => {
      map.off('move', update)
      map.off('zoom', update)
      map.off('resize', update)
      window.removeEventListener('resize', update)
    }
  }, [selectedMode, status])

  useEffect(() => {
    if (!isDraggingDivider) {
      return undefined
    }

    const handlePointerMove = (event) => {
      event.preventDefault()
      updateSplitFromPointer(event)
    }
    const handlePointerEnd = () => {
      setIsDraggingDivider(false)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerEnd)
    window.addEventListener('pointercancel', handlePointerEnd)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerEnd)
      window.removeEventListener('pointercancel', handlePointerEnd)
    }
  }, [isDraggingDivider, updateSplitFromPointer])

  useEffect(() => {
    if (
      selectedMode !== COMPARISON_METHOD_OPACITY ||
      status !== 'ready' ||
      !hasCurrentComparisonConfig ||
      !imageComparisonSource.current?.overlay?.canvas
    ) {
      return
    }

    const opacity = imageComparisonSource.current?.opacityInverted
      ? 1 - imageOpacity / 100
      : imageOpacity / 100

    mapLayersRef.current.land.setImageOpacity({
      landId,
      layerId: overlayLayerId,
      opacity,
    })
  }, [
    hasCurrentComparisonConfig,
    imageOpacity,
    landId,
    overlayLayerId,
    selectedMode,
    status,
  ])

  useEffect(() => {
    return () => {
      requestId.current += 1
      renderRequestId.current += 1
      clearComparisonLayers()
    }
  }, [clearComparisonLayers])

  const renderComparisonControls = () => (
    <div className='view-land-comparison-controls'>
      <div className='view-land-comparison-side'>
        <div
          className={`view-land-comparison-side__row ${baseHdDateHasMessage ? 'has-date-message' : ''}`.trim()}
        >
          <ComparisonSelect
            label={t('lands:view.comparison.fields.base_layer')}
            placeholder={t('lands:view.comparison.fields.choose_layer')}
            value={getComparisonLayerSelectValue(baseProduct, imageType)}
            onChange={handleBaseLayerChange}
            options={baseLayerOptions}
          />
          {isBaseImageLayer ? (
            <ComparisonCloudinessControl
              ariaLabel={t('lands:view.comparison.fields.base_cloudiness', {
                defaultValue: 'Nubosidad máxima de la capa base',
              })}
              className='view-land-comparison-cloudiness--compact'
              value={baseCloudinessFilter}
              onChange={setBaseCloudinessFilter}
            />
          ) : <span className='view-land-comparison-cloudiness-placeholder' aria-hidden='true' />}
          {isBaseHdImageLayer ? (
            <ComparisonHdDatePicker
              autoOpenSignal={baseDateAutoOpenSignal}
              cloudinessFilter={debouncedBaseCloudinessFilter}
              disabled={baseHdAcquisitionsState.status === 'loading' || preparingHdAcquisition != null}
              items={baseHdItems}
              label={t('lands:view.comparison.fields.base_date')}
              placeholder={t('lands:view.comparison.fields.choose_date')}
              value={baseAcquisitionId}
              onChange={(acquisitionId) => handleHdAcquisitionChange(
                DATE_PICKER_TARGET_BASE,
                acquisitionId
              )}
            />
          ) : (
            <ComparisonDatePicker
              autoOpenSignal={baseDateAutoOpenSignal}
              clearDates={baseClearDates}
              cloudinessFilter={debouncedBaseCloudinessFilter}
              cloudyDates={baseCloudyDates}
              disabled={!isBaseImageLayer}
              label={t('lands:view.comparison.fields.base_date')}
              placeholder={isBaseImageLayer ? t('lands:view.comparison.fields.choose_date') : '-'}
              value={isBaseImageLayer ? baseDate : ''}
              onChange={setBaseDate}
            />
          )}
        </div>
      </div>

      <div className='view-land-comparison-side'>
        <div
          className={`view-land-comparison-side__row ${compareHdDateHasMessage ? 'has-date-message' : ''}`.trim()}
        >
          <ComparisonSelect
            label={t('lands:view.comparison.fields.compare_layer')}
            placeholder={t('lands:view.comparison.fields.choose_layer')}
            value={getComparisonLayerSelectValue(compareProduct, compareImageType)}
            onChange={handleCompareLayerChange}
            options={compareLayerOptions}
          />
          {isCompareImageLayer ? (
            <ComparisonCloudinessControl
              ariaLabel={t('lands:view.comparison.fields.compare_cloudiness', {
                defaultValue: 'Nubosidad máxima de la capa comparada',
              })}
              className='view-land-comparison-cloudiness--compact'
              value={compareCloudinessFilter}
              onChange={setCompareCloudinessFilter}
            />
          ) : <span className='view-land-comparison-cloudiness-placeholder' aria-hidden='true' />}
          {isCompareHdImageLayer ? (
            <ComparisonHdDatePicker
              autoOpenSignal={compareDateAutoOpenSignal}
              cloudinessFilter={debouncedCompareCloudinessFilter}
              disabled={compareHdAcquisitionsState.status === 'loading' || preparingHdAcquisition != null}
              items={compareHdItems}
              label={t('lands:view.comparison.fields.compare_date')}
              placeholder={t('lands:view.comparison.fields.choose_date')}
              value={compareAcquisitionId}
              onChange={(acquisitionId) => handleHdAcquisitionChange(
                DATE_PICKER_TARGET_COMPARE,
                acquisitionId
              )}
            />
          ) : (
            <ComparisonDatePicker
              autoOpenSignal={compareDateAutoOpenSignal}
              clearDates={compareClearDates}
              cloudinessFilter={debouncedCompareCloudinessFilter}
              cloudyDates={compareCloudyDates}
              disabled={!isCompareImageLayer}
              label={t('lands:view.comparison.fields.compare_date')}
              placeholder={isCompareImageLayer ? t('lands:view.comparison.fields.choose_date') : '-'}
              value={isCompareImageLayer ? compareDate : ''}
              onChange={setCompareDate}
            />
          )}
        </div>
      </div>

      {(isBaseHdImageLayer || isCompareHdImageLayer) &&
        !selectingHdProduct && !hdAccessStatus.loading &&
        hdAccessStatus.state !== PLANET_HD_ACCESS_STATES.READY && (
        <div
          className={`view-land-product-first__access is-${hdAccessStatus.state}`}
          role='status'
        >
          {t(`images:hd_access_state.${hdAccessStatus.state}`, {
            defaultValue: hdAccessStatus.state,
          })}
        </div>
      )}

      <div
        className='view-land-comparison-mode-switch'
        role='group'
        aria-label={t('lands:view.menu.comparison')}
      >
        {comparisonMethods.map((mode) => (
          <ComparisonModeButton
            key={mode.id}
            active={selectedMode === mode.id}
            enabled={mode.enabled}
            onClick={() => setSelectedMode(mode.id)}
            title={mode.title}
          />
        ))}
      </div>

      {status === 'ready' &&
        hasCurrentComparisonConfig &&
        selectedMode === COMPARISON_METHOD_SWIPE && (
          <>
            <ComparisonRange
              label={t('lands:view.comparison.fields.swipe')}
              value={splitPercent}
              onChange={(event) => setSplitPercent(Number(event.target.value))}
            />
            <div className='view-land-comparison-swipe-labels'>
              <span>{getLayerSelectionLabel(imageType, baseDate, baseProduct)}</span>
              <span>{getLayerSelectionLabel(compareImageType, compareDate, compareProduct)}</span>
            </div>
          </>
        )}

      {status === 'ready' &&
        hasCurrentComparisonConfig &&
        selectedMode === COMPARISON_METHOD_OPACITY && (
        <ComparisonRange
          label={t('lands:view.comparison.fields.layer_opacity', {
            value: toDecimalCommaLabel(imageOpacity),
          })}
          value={imageOpacity}
          onChange={(event) => setImageOpacity(Number(event.target.value))}
        />
      )}
    </div>
  )

  return (
    <>
      {showDatePickerLoader && (
        <div
          className='view-land-comparison-date-loader'
          role='status'
          aria-label={t('lands:view.comparison.loading')}
        >
          <span
            className='spinner-border text-primary view-land-comparison-date-loader__spinner'
            role='presentation'
          >
            <span className='visually-hidden'>{t('lands:view.comparison.loading')}</span>
          </span>
        </div>
      )}

      {selectedMode === COMPARISON_METHOD_SWIPE && status === 'ready' && dividerGuideGeometry && (
        <div
          className='view-land-comparison-divider-guide'
          style={{
            height: dividerGuideGeometry.length,
            left: dividerGuideGeometry.x,
            top: dividerGuideGeometry.y,
            transform: `translate(-50%, 0) rotate(${dividerGuideGeometry.angle}deg)`,
          }}
        >
          <button
            type='button'
            aria-label={t('lands:view.comparison.fields.divider_handle')}
            className={`view-land-comparison-divider-handle ${
              isDraggingDivider ? 'dragging' : ''
            }`.trim()}
            onPointerDown={(event) => {
              event.preventDefault()
              event.stopPropagation()
              event.currentTarget.setPointerCapture?.(event.pointerId)
              setIsDraggingDivider(true)
              updateSplitFromPointer(event)
            }}
            onPointerUp={(event) => {
              event.preventDefault()
              event.stopPropagation()
              event.currentTarget.releasePointerCapture?.(event.pointerId)
              setIsDraggingDivider(false)
            }}
            onPointerCancel={() => setIsDraggingDivider(false)}
          >
            <span className='view-land-comparison-divider-handle__grip' />
          </button>
        </div>
      )}

    <div className='view-land-comparison-panel'>
      {renderComparisonControls()}

      {status === 'loading' && (
        <div className='view-land-comparison-inline-status' role='status'>
          <span>{t('lands:view.comparison.loading')}</span>
          <span
            className='spinner-border spinner-border-sm text-primary view-land-comparison-inline-status__spinner'
            aria-hidden='true'
          />
        </div>
      )}

      {status === 'error' && (
        <div className='alert alert-danger view-land-comparison-alert'>{errorMessage}</div>
      )}
    </div>
    </>
  )
}

export default Comparison
